import React from 'react';
import PropTypes from 'prop-types';
import { TrendingUp, TrendingDown, Minus, Sparkles, Target, Zap, Award, AlertTriangle } from 'lucide-react';

/**
 * Data Storytelling Components
 * Transform raw data into compelling narratives with visual impact
 */

/**
 * InsightCard - Primary insight with visual emphasis
 */
export const InsightCard = ({
  insight,
  metric,
  context,
  trend,
  trendDirection = 'neutral',
  icon: CustomIcon,
  variant = 'default',
  className = '',
}) => {
  const TrendIcon = trendDirection === 'up' ? TrendingUp : trendDirection === 'down' ? TrendingDown : Minus;
  const DefaultIcon = variant === 'success' ? Award : variant === 'warning' ? AlertTriangle : Sparkles;

  // Handle both JSX elements and component types for icon prop
  const isJsxElement = React.isValidElement(CustomIcon);
  const IconComponent = isJsxElement ? null : (CustomIcon || DefaultIcon);

  const variants = {
    default: 'from-primary-500/10 to-transparent border-primary-500/20',
    success: 'from-success-500/10 to-transparent border-success-500/20',
    warning: 'from-warning-500/10 to-transparent border-warning-500/20',
    error: 'from-error-500/10 to-transparent border-error-500/20',
    info: 'from-secondary-500/10 to-transparent border-secondary-500/20',
  };

  const iconColors = {
    default: 'bg-primary-500/20 text-primary-400',
    success: 'bg-success-500/20 text-success-400',
    warning: 'bg-warning-500/20 text-warning-400',
    error: 'bg-error-500/20 text-error-400',
    info: 'bg-secondary-500/20 text-secondary-400',
  };

  const trendColors = {
    up: 'text-success-400',
    down: 'text-error-400',
    neutral: 'text-gray-400',
  };

  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${variants[variant]} border p-5 sm:p-6 ${className}`}>
      <div className="flex items-start gap-4">
        <div className={`p-2.5 rounded-xl ${iconColors[variant]}`}>
          {isJsxElement ? CustomIcon : <IconComponent className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base sm:text-lg text-white leading-relaxed">{insight}</p>
          {(metric || trend) && (
            <div className="flex items-baseline gap-4 mt-3">
              {metric && (
                <span className="text-3xl sm:text-4xl font-bold text-white tabular-nums">{metric}</span>
              )}
              {trend && (
                <span className={`flex items-center gap-1 text-sm font-medium ${trendColors[trendDirection]}`}>
                  <TrendIcon className="w-4 h-4" />
                  {trend}
                </span>
              )}
            </div>
          )}
          {context && (
            <p className="text-sm text-gray-400 mt-2">{context}</p>
          )}
        </div>
      </div>
    </div>
  );
};

InsightCard.propTypes = {
  insight: PropTypes.string.isRequired,
  metric: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  context: PropTypes.string,
  trend: PropTypes.string,
  trendDirection: PropTypes.oneOf(['up', 'down', 'neutral']),
  icon: PropTypes.elementType,
  variant: PropTypes.oneOf(['default', 'success', 'warning', 'error', 'info']),
  className: PropTypes.string,
};

/**
 * MetricHighlight - Prominent single metric display
 */
export const MetricHighlight = ({
  value,
  label,
  unit,
  change,
  changeLabel,
  size = 'lg',
  align = 'center',
  className = '',
}) => {
  const sizeStyles = {
    sm: { value: 'text-2xl', label: 'text-xs', change: 'text-xs' },
    md: { value: 'text-3xl sm:text-4xl', label: 'text-sm', change: 'text-sm' },
    lg: { value: 'text-4xl sm:text-5xl lg:text-6xl', label: 'text-sm sm:text-base', change: 'text-sm' },
    xl: { value: 'text-5xl sm:text-6xl lg:text-7xl', label: 'text-base sm:text-lg', change: 'text-base' },
  };

  const alignStyles = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  const isPositive = change && !change.startsWith('-');
  const changeColor = isPositive ? 'text-success-400' : change ? 'text-error-400' : 'text-gray-400';

  return (
    <div className={`${alignStyles[align]} ${className}`}>
      <div className={`${sizeStyles[size].value} font-bold text-white tracking-tight tabular-nums`}>
        {value}
        {unit && <span className="text-[0.5em] text-gray-400 ml-1">{unit}</span>}
      </div>
      {change && (
        <div className={`${sizeStyles[size].change} ${changeColor} font-medium mt-1 flex items-center justify-${align === 'center' ? 'center' : align} gap-1`}>
          {isPositive && <TrendingUp className="w-3.5 h-3.5" />}
          {change.startsWith('-') && <TrendingDown className="w-3.5 h-3.5" />}
          {change}
          {changeLabel && <span className="text-gray-500 ml-1">{changeLabel}</span>}
        </div>
      )}
      <div className={`${sizeStyles[size].label} text-gray-400 uppercase tracking-wider mt-1`}>
        {label}
      </div>
    </div>
  );
};

MetricHighlight.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
  unit: PropTypes.string,
  change: PropTypes.string,
  changeLabel: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  align: PropTypes.oneOf(['left', 'center', 'right']),
  className: PropTypes.string,
};

/**
 * ComparisonBar - Visual comparison of two values
 */
export const ComparisonBar = ({
  leftValue,
  rightValue,
  leftLabel,
  rightLabel,
  leftColor = '#a78bfa',
  rightColor = '#60a5fa',
  className = '',
}) => {
  const total = leftValue + rightValue;
  const leftPercent = total > 0 ? (leftValue / total) * 100 : 50;

  return (
    <div className={className}>
      <div className="flex justify-between text-sm mb-2">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: leftColor }} />
          <span className="text-gray-300">{leftLabel}</span>
          <span className="text-white font-semibold tabular-nums">{leftValue}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-white font-semibold tabular-nums">{rightValue}</span>
          <span className="text-gray-300">{rightLabel}</span>
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: rightColor }} />
        </div>
      </div>
      <div className="h-3 rounded-full bg-white/[0.04] overflow-hidden flex">
        <div
          className="h-full rounded-l-full transition-all duration-500"
          style={{ width: `${leftPercent}%`, backgroundColor: leftColor }}
        />
        <div
          className="h-full rounded-r-full transition-all duration-500"
          style={{ width: `${100 - leftPercent}%`, backgroundColor: rightColor }}
        />
      </div>
    </div>
  );
};

ComparisonBar.propTypes = {
  leftValue: PropTypes.number.isRequired,
  rightValue: PropTypes.number.isRequired,
  leftLabel: PropTypes.string.isRequired,
  rightLabel: PropTypes.string.isRequired,
  leftColor: PropTypes.string,
  rightColor: PropTypes.string,
  className: PropTypes.string,
};

/**
 * Milestone - Achievement or milestone display
 */
export const Milestone = ({
  title,
  description,
  date,
  icon: Icon = Target,
  status = 'complete',
  className = '',
}) => {
  const statusStyles = {
    complete: 'bg-success-500/20 text-success-400 border-success-500/30',
    active: 'bg-primary-500/20 text-primary-400 border-primary-500/30 animate-pulse',
    pending: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };

  return (
    <div className={`flex items-start gap-3 ${className}`}>
      <div className={`p-2 rounded-xl border ${statusStyles[status]}`}>
        {React.isValidElement(Icon) ? Icon : <Icon className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium text-white">{title}</span>
          {date && <span className="text-xs text-gray-500">{date}</span>}
        </div>
        {description && (
          <p className="text-sm text-gray-400 mt-0.5">{description}</p>
        )}
      </div>
    </div>
  );
};

Milestone.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  date: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
  status: PropTypes.oneOf(['complete', 'active', 'pending']),
  className: PropTypes.string,
};

/**
 * QuickStat - Compact inline statistic
 */
export const QuickStat = ({
  value,
  label,
  icon: Icon,
  color = 'default',
  className = '',
}) => {
  const colorStyles = {
    default: 'text-white',
    primary: 'text-primary-400',
    success: 'text-success-400',
    warning: 'text-warning-400',
    error: 'text-error-400',
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {Icon && <Icon className={`w-4 h-4 ${colorStyles[color]}`} />}
      <span className={`font-semibold ${colorStyles[color]} tabular-nums`}>{value}</span>
      <span className="text-gray-500 text-sm">{label}</span>
    </div>
  );
};

QuickStat.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  color: PropTypes.oneOf(['default', 'primary', 'success', 'warning', 'error']),
  className: PropTypes.string,
};

/**
 * NarrativeBlock - Longer form narrative text
 */
export const NarrativeBlock = ({
  title,
  children,
  highlight,
  className = '',
}) => (
  <div className={className}>
    {title && (
      <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-2">{title}</h4>
    )}
    <div className="text-gray-300 leading-relaxed space-y-3">
      {children}
    </div>
    {highlight && (
      <div className="mt-4 p-4 rounded-xl bg-primary-500/10 border border-primary-500/20">
        <div className="flex items-start gap-3">
          <Zap className="w-5 h-5 text-primary-400 mt-0.5" />
          <p className="text-primary-200">{highlight}</p>
        </div>
      </div>
    )}
  </div>
);

NarrativeBlock.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  highlight: PropTypes.string,
  className: PropTypes.string,
};

/**
 * Timeline - Vertical timeline of events
 */
export const Timeline = ({ items, className = '' }) => (
  <div className={`relative pl-6 ${className}`}>
    <div className="absolute left-2 top-2 bottom-2 w-px bg-white/[0.06]" />
    <div className="space-y-4">
      {items.map((item, index) => (
        <div key={index} className="relative">
          <div className={`absolute -left-4 w-2.5 h-2.5 rounded-full border-2 ${
            item.active ? 'bg-primary-500 border-primary-400' : 'bg-surface border-white/20'
          }`} />
          <Milestone {...item} />
        </div>
      ))}
    </div>
  </div>
);

Timeline.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    date: PropTypes.string,
    icon: PropTypes.elementType,
    status: PropTypes.oneOf(['complete', 'active', 'pending']),
    active: PropTypes.bool,
  })).isRequired,
  className: PropTypes.string,
};

export default {
  InsightCard,
  MetricHighlight,
  ComparisonBar,
  Milestone,
  QuickStat,
  NarrativeBlock,
  Timeline,
};
